/**
 * Utilidades genéricas para inputs y validación de caracteres en tiempo real.
 * Basado en Constants.java y Utilidades.java del legacy GWT.
 * 
 * Uso en templates:
 *   (keypress)="onlyLettersKeypress($event)"
 *   (input)="onlyLettersInput($event)"
 */

import { cleanString } from './format-utils';

// ============================================
// REGEX PATTERNS - Patrones de validación
// ============================================

/** Solo letras (incluyendo acentos y ñ) */
export const REGEX_ONLY_LETTERS = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]*$/;

/** Solo números */
export const REGEX_ONLY_NUMBERS = /^[0-9]*$/;

/** Alfanumérico (letras, números, espacios, acentos) - equivale a REGEX_ALPHANUMERIC del legacy */
export const REGEX_ALPHANUMERIC = /^[a-zA-Z0-9\s\u00e1\u00e9\u00ed\u00f3\u00fa\u00c1\u00c9\u00cd\u00d3\u00da\u00f1\u00d1\u00fc\u00dc]*$/;

/** Alfanumérico extendido (con puntos, comas, guiones) - equivale a REGEX_ALPHANUMERIC_PS del legacy */
export const REGEX_ALPHANUMERIC_EXTENDED = /^[a-zA-Z0-9\s\u00e1\u00e9\u00ed\u00f3\u00fa\u00c1\u00c9\u00cd\u00d3\u00da\u00f1\u00d1\u00fc\u00dc.,\-_:/]*$/;

/** Números de identificación (números, guiones, puntos) */
export const REGEX_IDENTIFICACION = /^[0-9\-\.]*$/;

/**
 * Número de identificación de los buscadores (empleados, beneficiarios, aseguradoras,
 * personas): letras y dígitos, sin espacios ni '/', '.', '-'. Admite pasaportes.
 * Legacy: IdentificationViewImpl.setRegex() (Constants.REGEX_ALPHANUMERIC); misma
 * allowlist que SearchRequestValidator.validateIdentificacion del backend.
 */
export const REGEX_IDENTIFICACION_BUSQUEDA = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü]*$/;

/** Longitud máxima del número de identificación en los buscadores (backend: 20). */
export const MAX_IDENTIFICACION_BUSQUEDA = 20;

/** Email */
export const REGEX_EMAIL = /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){1,8}$/;

/** Números decimales (hasta 7 enteros, 2 decimales) - equivale a REGEX_DECIMAL_NUMBERS */
export const REGEX_DECIMAL_NUMBERS = /^(\d{0,7}(\.\d{0,2})?)$/;

/** Números de costo (hasta 8 enteros, 2 decimales) - equivale a REGEX_COST_NUMBERS */
export const REGEX_COST_NUMBERS = /^(\d{0,8}(\.\d{0,2})?)$/;

/** Porcentaje (hasta 3 enteros, 2 decimales) - equivale a REGEX_PERCENT_NUMBERS */
export const REGEX_PERCENT_NUMBERS = /^(\d{0,3}(\.\d{0,2})?)$/;

/** Salario - números con punto decimal opcional */
export const REGEX_SALARIO = /^[0-9]+[.]?[0-9]*$/;

/** Fechas formato YYYY/MM/DD o YYYY.MM.DD */
export const REGEX_DATES = /^(19|20)\d\d[/\-.](0[1-9]|1[012])[/\-.](0[1-9]|[12][0-9]|3[01])$/;

/** Formato de imagen */
export const REGEX_IMAGE_FORMAT = /^[^\r\n]+\.(?:jpe?g|gif|png)$/i;

/** Color hexadecimal */
export const REGEX_HEX_COLOR = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;

/** Teléfono (números, espacios, guiones, paréntesis, +) */
export const REGEX_PHONE = /^[0-9\s\-\(\)\+]*$/;

/** Longitudes máximas usadas por contacto (paridad legacy) */
export const CONTACT_MAX_LENGTHS = {
  NOMBRE: 50,
  CARGO: 30,
  TELEFONO: 30,
  EMAIL: 100
};

// ============================================
// KEYPRESS HANDLERS - Previenen caracteres no permitidos
// ============================================

/**
 * Handler para keypress que solo permite letras (y espacios con acentos)
 * Uso: (keypress)="onlyLettersKeypress($event)"
 */
export function onlyLettersKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  // Permitir teclas de control (Backspace, Delete, arrows, etc.)
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  // Solo letras, espacios y acentos
  return /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]$/.test(char);
}

/**
 * Handler para keypress que solo permite números
 * Uso: (keypress)="onlyNumbersKeypress($event)"
 */
export function onlyNumbersKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[0-9]$/.test(char);
}

/**
 * Handler para keypress de identificación (números, guiones, puntos)
 * Uso: (keypress)="identificacionKeypress($event)"
 */
export function identificacionKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[0-9\-\.]$/.test(char);
}

/**
 * Handler para keypress del número de identificación de los buscadores
 * (letras y dígitos, sin espacios ni separadores). Ver REGEX_IDENTIFICACION_BUSQUEDA.
 * Uso: (keypress)="identificacionBusquedaKeypress($event)"
 */
export function identificacionBusquedaKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü]$/.test(char);
}

/**
 * Handler para keypress alfanumérico (letras, números, espacios, acentos)
 * Uso: (keypress)="alphanumericKeypress($event)"
 */
export function alphanumericKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ]$/.test(char);
}

/**
 * Handler para keypress de teléfono
 * Uso: (keypress)="phoneKeypress($event)"
 */
export function phoneKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[0-9\s\-\(\)\+]$/.test(char);
}

/**
 * Handler para keypress de decimales (números y punto)
 * Uso: (keypress)="decimalKeypress($event)"
 */
export function decimalKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  return /^[0-9\.]$/.test(char);
}

/**
 * Handler para keypress de email
 * Uso: (keypress)="emailKeypress($event)"
 */
export function emailKeypress(event: KeyboardEvent): boolean {
  const char = event.key;
  if (event.ctrlKey || event.metaKey || char.length > 1) {
    return true;
  }
  // Permitir caracteres válidos para email
  return /^[a-zA-Z0-9._%+\-@]$/.test(char);
}

// ============================================
// INPUT HANDLERS - Sanitizan el valor después de escribir
// ============================================

/**
 * Handler para input que solo permite letras
 * Uso: (input)="onlyLettersInput($event)"
 */
export function onlyLettersInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  input.value = sanitizeLettersOnly(input.value);
}

/**
 * Handler para input que solo permite números
 * Uso: (input)="onlyNumbersInput($event)"
 */
export function onlyNumbersInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  input.value = sanitizeNumbersOnly(input.value);
}

/**
 * Handler para input de identificación
 * Uso: (input)="identificacionInput($event)"
 */
export function identificacionInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  input.value = sanitizeIdentificacion(input.value);
}

/**
 * Handler para input alfanumérico
 * Uso: (input)="alphanumericInput($event)"
 */
export function alphanumericInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  input.value = sanitizeAlphanumeric(input.value);
}

// ============================================
// SANITIZE FUNCTIONS - Limpian valores
// ============================================

/** Elimina todo excepto letras, espacios y acentos */
export function sanitizeLettersOnly(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
}

/** Elimina todo excepto números */
export function sanitizeNumbersOnly(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^0-9]/g, '');
}

/** Elimina todo excepto números, guiones y puntos */
export function sanitizeIdentificacion(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^0-9\-\.]/g, '');
}

/**
 * Número de identificación de los buscadores: elimina primero el contenido XSS
 * (sanitizeText, para que el texto de un script no sobreviva como letras), conserva
 * solo letras y dígitos (sin espacios, '/', '.', '-') y lo pasa a mayúsculas.
 * Legacy: IdentificationViewImpl.getDataFromFields() — nmidentificacion.toUpperCase().
 */
export function sanitizeIdentificacionBusqueda(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return sanitizeText(value).replace(/[^A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü]/g, '').toUpperCase();
}

/** Elimina todo excepto alfanumérico, espacios y acentos */
export function sanitizeAlphanumeric(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ]/g, '');
}

/** Elimina todo excepto números, punto decimal */
export function sanitizeDecimal(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  // Permitir solo un punto decimal
  const sanitized = value.replace(/[^0-9.]/g, '');
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  return sanitized;
}

/** Elimina todo excepto caracteres válidos para teléfono */
export function sanitizePhone(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^0-9\s\-\(\)\+]/g, '');
}

/** 
 * Sanitiza y valida email.
 * Elimina caracteres no válidos y espacios.
 * Solo permite: a-zA-Z0-9._%+-@
 */
export function sanitizeEmail(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  // Eliminar espacios y caracteres no válidos para email
  return value.replace(/[^a-zA-Z0-9._%+\-@]/g, '').toLowerCase();
}

/**
 * Igual que {@link sanitizeEmail} pero conserva mayúsculas y minúsculas.
 * Legacy (AdminContactosViewImpl, ListaContactosViewImpl, GestionarContactosInformeViewImpl): el correo
 * del contacto se guarda tal como se digita; solo el nombre se pasa a mayúsculas.
 */
export function sanitizeEmailContacto(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/[^a-zA-Z0-9._%+\-@]/g, '');
}

/**
 * Valida si un email tiene formato correcto.
 * Uso: if (isValidEmail(email)) { ... }
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return REGEX_EMAIL.test(value);
}

/**
 * Handler para input de email con sanitización.
 * Uso: (input)="emailInput($event)"
 */
export function emailInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  input.value = sanitizeEmail(input.value);
}

// ============================================
// TIPOS DE INPUT - Para configuración de componentes
// ============================================

export type InputType = 
  | 'text'           // Texto libre
  | 'letters'        // Solo letras
  | 'numbers'        // Solo números
  | 'alphanumeric'   // Alfanumérico
  | 'identificacion' // Número de identificación
  | 'email'          // Email
  | 'phone'          // Teléfono
  | 'decimal'        // Números decimales
  | 'currency';      // Moneda

/**
 * Obtiene el handler de keypress según el tipo de input
 */
export function getKeypressHandler(type: InputType): (event: KeyboardEvent) => boolean {
  switch (type) {
    case 'letters': return onlyLettersKeypress;
    case 'numbers': return onlyNumbersKeypress;
    case 'alphanumeric': return alphanumericKeypress;
    case 'identificacion': return identificacionKeypress;
    case 'email': return emailKeypress;
    case 'phone': return phoneKeypress;
    case 'decimal':
    case 'currency': return decimalKeypress;
    default: return () => true;
  }
}

/**
 * Obtiene el handler de input según el tipo
 */
export function getInputHandler(type: InputType): (event: Event) => void {
  switch (type) {
    case 'letters': return onlyLettersInput;
    case 'numbers': return onlyNumbersInput;
    case 'alphanumeric': return alphanumericInput;
    case 'identificacion': return identificacionInput;
    default: return () => {};
  }
}

/**
 * Obtiene la expresión regular según el tipo
 */
export function getRegexForType(type: InputType): RegExp {
  switch (type) {
    case 'letters': return REGEX_ONLY_LETTERS;
    case 'numbers': return REGEX_ONLY_NUMBERS;
    case 'alphanumeric': return REGEX_ALPHANUMERIC;
    case 'identificacion': return REGEX_IDENTIFICACION;
    case 'email': return REGEX_EMAIL;
    case 'phone': return REGEX_PHONE;
    case 'decimal': return REGEX_DECIMAL_NUMBERS;
    case 'currency': return REGEX_COST_NUMBERS;
    default: return /.*/;
  }
}

// ============================================
// XSS SANITIZATION - Protección contra Cross-Site Scripting
// ============================================

/**
 * Patrones XSS peligrosos que deben ser eliminados.
 * Incluye: scripts, eventos JS, iframes, objetos embebidos, etc.
 */
const XSS_PATTERNS: RegExp[] = [
  // Script tags (incluyendo variantes con espacios y atributos)
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /<script[^>]*>/gi,
  /<\/script>/gi,
  
  // Eventos JavaScript inline (onclick, onerror, onload, etc.)
  /\bon\w+\s*=\s*["'][^"']*["']/gi,
  /\bon\w+\s*=\s*[^\s>]+/gi,
  
  // Tags peligrosos
  /<iframe[^>]*>.*?<\/iframe>/gi,
  /<iframe[^>]*>/gi,
  /<object[^>]*>.*?<\/object>/gi,
  /<object[^>]*>/gi,
  /<embed[^>]*>/gi,
  /<form[^>]*>.*?<\/form>/gi,
  /<form[^>]*>/gi,
  /<input[^>]*>/gi,
  /<link[^>]*>/gi,
  /<meta[^>]*>/gi,
  /<style[^>]*>.*?<\/style>/gi,
  /<style[^>]*>/gi,
  
  // JavaScript en URLs
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /data\s*:[^,]*base64/gi,
  
  // Expresiones peligrosas
  /expression\s*\(/gi,
  /eval\s*\(/gi,
];

/**
 * Patrones SQL sospechosos usados por `containsSQL()`/`containsDangerousContent()`
 * para RECHAZAR celdas de carga masiva (Excel) con contenido peligroso.
 * NO se usan para sanitizar formularios en tiempo real: una lista negra de
 * palabras clave aplicada tecla a tecla borra vocabulario español legítimo
 * (p. ej. "orden", "setenta") sin aportar protección real. La defensa contra
 * inyección SQL en formularios es responsabilidad del backend (parámetros
 * enlazados en el acceso a datos + `@SafeText`). Ver seguridad-girop
 * SKILL.md §2.1/§3.
 */
const SQL_PATTERNS: RegExp[] = [
  // Comandos DML/DDL básicos (palabras completas)
  /\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|TRUNCATE)\b/gi,
  /\b(EXEC|EXECUTE|MERGE|CALL)\b/gi,
  
  // Cláusulas SQL
  /\b(FROM|WHERE|AND|OR|UNION|JOIN|INTO|VALUES|SET)\b/gi,
  /\b(ORDER\s+BY|GROUP\s+BY|HAVING)\b/gi,
  
  // Funciones peligrosas
  /\b(CONCAT|SUBSTRING|CHAR|ASCII|BENCHMARK|SLEEP|WAITFOR)\b/gi,
  /\b(LOAD_FILE|OUTFILE|DUMPFILE)\b/gi,
  
  // Comentarios SQL (usados para bypass)
  /--\s*/g,
  /\/\*[\s\S]*?\*\//g,
  /#.*/g,
  
  // Operadores de comparación con comillas (típico de inyección)
  /'\s*(=|<|>|!=|<>)\s*'/g,
  /"\s*(=|<|>|!=|<>)\s*"/g,
  
  // OR/AND con expresiones siempre verdaderas (1=1, 'a'='a', etc.)
  /\bOR\s+[\d'"][^\s]*\s*=\s*[\d'"]/gi,
  /\bAND\s+[\d'"][^\s]*\s*=\s*[\d'"]/gi,
  
  // Punto y coma seguido de comandos (encadenamiento)
  /;\s*(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|EXEC)/gi,
  
  // Funciones de sistema
  /\b(INFORMATION_SCHEMA|SYS\.|SYSOBJECTS|SYSCOLUMNS)\b/gi,
  /\b(@@VERSION|@@SERVERNAME|DB_NAME|USER|CURRENT_USER)\b/gi,
  
  // xp_cmdshell y procedimientos peligrosos
  /\bxp_\w+/gi,
  /\bsp_\w+/gi,
];

/**
 * Sanitiza texto eliminando contenido XSS (scripts, eventos JS, tags peligrosos).
 * Preserva: letras, números, espacios, puntuación básica, acentos, ñ.
 * No filtra comandos SQL: una lista negra de palabras clave aplicada en cada
 * tecla borra vocabulario español legítimo (p. ej. "orden", "setenta") sin
 * aportar protección real. La defensa contra inyección SQL es responsabilidad
 * del backend (parámetros enlazados + `@SafeText`, ver seguridad-girop
 * SKILL.md §2.1/§3).
 *
 * Uso:
 *   (input)="campo = sanitizeText(campo)"
 *   [value]="sanitizeText(valorOriginal)"
 *
 * @param value - Texto a sanitizar
 * @returns Texto limpio sin contenido XSS
 */
export function sanitizeText(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  let sanitized = value;

  // Eliminar patrones XSS peligrosos
  for (const pattern of XSS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Eliminar cualquier tag HTML restante pero preservar el contenido
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // Limpiar múltiples espacios consecutivos
  sanitized = sanitized.replace(/\s{2,}/g, ' ');

  return sanitized.trim();
}

/**
 * Variante de `sanitizeText` que preserva espacios al inicio/fin.
 * Útil para permitir que el usuario teclee espacios en tiempo real
 * (no recorta el espacio final mientras el usuario escribe). La
 * sanitización XSS se mantiene; no filtra comandos SQL (ver `sanitizeText`).
 */
export function sanitizeTextPreserveSpaces(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  let sanitized = value;

  // Eliminar patrones XSS peligrosos
  for (const pattern of XSS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Eliminar cualquier tag HTML restante pero preservar el contenido
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // Limpiar múltiples espacios consecutivos (mantener espacios simples y finales)
  sanitized = sanitized.replace(/\s{2,}/g, ' ');

  // NOTA: no aplicamos .trim() aquí para no eliminar el espacio final
  // mientras el usuario está escribiendo.
  return sanitized;
}

/**
 * Nombre de contacto (principal del cumplimiento y contactos adicionales).
 * Legacy: `getTextBoxChangeHandlerPreserveCase(true)` → `Utilidades.cleanString` (tildes y ñ a ASCII,
 * solo letras y espacios) con `Constants.REGEX_NAMES` (`[a-zA-Z\s]+`). El resultado siempre cumple
 * `@SafeText(PERSON_NAME)` del backend. Conserva el espacio final para poder teclear nombres compuestos.
 */
export function sanitizeNombreContacto(value: string | null | undefined): string {
  return cleanString(sanitizeTextPreserveSpaces(value)).replace(/\s+/g, ' ');
}

/**
 * Sanitiza texto para campos textarea (permite saltos de línea).
 * Similar a sanitizeText pero preserva \n y \r.
 * Elimina contenido XSS. No filtra comandos SQL (ver `sanitizeText`).
 *
 * Uso para observaciones, comentarios, descripciones:
 *   (input)="observaciones = sanitizeTextarea(observaciones)"
 *
 * @param value - Texto a sanitizar
 * @returns Texto limpio preservando saltos de línea
 */
export function sanitizeTextarea(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  let sanitized = value;

  // Eliminar patrones XSS peligrosos
  for (const pattern of XSS_PATTERNS) {
    sanitized = sanitized.replace(pattern, '');
  }

  // Eliminar cualquier tag HTML restante pero preservar el contenido
  sanitized = sanitized.replace(/<[^>]+>/g, '');

  // No eliminar saltos de línea en textareas
  // Solo limpiar múltiples espacios en la misma línea
  sanitized = sanitized.replace(/[^\S\r\n]{2,}/g, ' ');

  return sanitized;
}

/**
 * Elimina completamente todos los tags HTML de un texto.
 * Útil para mostrar contenido que podría contener HTML escapado.
 * 
 * @param value - Texto con posible HTML
 * @returns Texto sin ningún tag HTML
 */
export function stripHtmlTags(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.replace(/<[^>]*>/g, '').trim();
}

/**
 * Formato de dinero legacy: `Utilidades.formatAsMoney()`
 * (`"$" + NumberFormat.getFormat("#,###,###.##").format(value)`) — agrupa
 * miles, hasta 2 decimales opcionales (sin ceros de relleno), prefijo `$`
 * literal (no símbolo de moneda por locale). Acepta `number` o `string`
 * (el legado también formatea campos de nómina que llegan como texto).
 * Retorna `''` si el valor es nulo/vacío o no numérico — mismo criterio que
 * `Utilidades.formatAsMoney(String)`, que devuelve `$0` solo si el parseo
 * falla silenciosamente; aquí se prefiere '' para no fabricar un valor.
 */
export function formatAsMoneyLegacy(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numero = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numero)) {
    return '';
  }
  const formateado = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numero);
  return '$' + formateado;
}

/**
 * Handler para input de texto libre con sanitización XSS.
 * Uso: (input)="sanitizeTextInput($event)"
 */
export function sanitizeTextInput(event: Event): void {
  const input = event.target as HTMLInputElement | HTMLTextAreaElement;
  const isTextarea = input.tagName.toLowerCase() === 'textarea';
  input.value = isTextarea ? sanitizeTextarea(input.value) : sanitizeText(input.value);
}

/**
 * Verifica si un texto contiene posible contenido XSS.
 * Útil para validaciones antes de enviar al servidor.
 * 
 * @param value - Texto a verificar
 * @returns true si contiene contenido sospechoso
 */
export function containsXSS(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  
  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(value)) {
      return true;
    }
    // Reset lastIndex para patrones con flag 'g'
    pattern.lastIndex = 0;
  }
  
  // Verificar tags HTML genéricos
  return /<[^>]+>/.test(value);
}

/**
 * Verifica si un texto contiene posible SQL Injection.
 * Útil para validaciones antes de enviar al servidor.
 * 
 * @param value - Texto a verificar
 * @returns true si contiene comandos SQL sospechosos
 */
export function containsSQL(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  
  for (const pattern of SQL_PATTERNS) {
    if (pattern.test(value)) {
      return true;
    }
    // Reset lastIndex para patrones con flag 'g'
    pattern.lastIndex = 0;
  }
  
  return false;
}

/**
 * Verifica si un texto contiene XSS o SQL Injection.
 * Combinación de containsXSS y containsSQL.
 * 
 * @param value - Texto a verificar
 * @returns true si contiene contenido peligroso
 */
export function containsDangerousContent(value: string | null | undefined): boolean {
  return containsXSS(value) || containsSQL(value);
}

// ============================================================================
// VALIDACIONES PARA CARGA MASIVA DE EXCEL
// ============================================================================

/**
 * Resultado de validación de celda Excel
 */
export interface ExcelCellValidation {
  /** Indica si la celda es válida */
  valido: boolean;
  /** Valor sanitizado (si es string) */
  valorSanitizado?: string | number | Date;
  /** Mensaje de error si no es válido */
  error?: string;
}

/**
 * Valida que un valor de celda Excel sea numérico.
 * Acepta: números, strings numéricos, null/undefined (retorna inválido).
 * 
 * @param value - Valor de la celda
 * @param nombreColumna - Nombre de la columna para mensajes de error
 * @param requerido - Si es requerido (default: true)
 * @returns Resultado de validación con el número parseado
 */
export function validarCeldaNumerica(
  value: unknown,
  nombreColumna: string,
  requerido = true
): ExcelCellValidation {
  // Null o undefined
  if (value === null || value === undefined || value === '') {
    if (requerido) {
      return { valido: false, error: `${nombreColumna} es requerido` };
    }
    return { valido: true, valorSanitizado: undefined };
  }

  // Ya es número
  if (typeof value === 'number') {
    if (isNaN(value)) {
      return { valido: false, error: `${nombreColumna} no es un número válido` };
    }
    return { valido: true, valorSanitizado: value };
  }

  // Es string - intentar parsear
  if (typeof value === 'string') {
    // Verificar contenido peligroso primero
    if (containsDangerousContent(value)) {
      return { valido: false, error: `${nombreColumna} contiene caracteres no permitidos` };
    }

    const trimmed = value.trim();
    if (trimmed === '' && !requerido) {
      return { valido: true, valorSanitizado: undefined };
    }

    // Intentar parsear como número
    const numero = Number(trimmed);
    if (isNaN(numero)) {
      return { valido: false, error: `${nombreColumna} debe ser numérico` };
    }
    return { valido: true, valorSanitizado: numero };
  }

  return { valido: false, error: `${nombreColumna} tiene un tipo de dato inválido` };
}

/**
 * Valida y sanitiza un valor de celda Excel como string.
 * Aplica sanitización XSS y SQL Injection.
 * 
 * @param value - Valor de la celda
 * @param nombreColumna - Nombre de la columna para mensajes de error
 * @param requerido - Si es requerido (default: true)
 * @param maxLength - Longitud máxima permitida (default: 500)
 * @returns Resultado de validación con el string sanitizado
 */
export function validarCeldaString(
  value: unknown,
  nombreColumna: string,
  requerido = true,
  maxLength = 500
): ExcelCellValidation {
  // Null o undefined
  if (value === null || value === undefined) {
    if (requerido) {
      return { valido: false, error: `${nombreColumna} es requerido` };
    }
    return { valido: true, valorSanitizado: '' };
  }

  // Convertir a string
  const str = String(value).trim();

  if (str === '' && requerido) {
    return { valido: false, error: `${nombreColumna} es requerido` };
  }

  // Verificar longitud
  if (str.length > maxLength) {
    return { valido: false, error: `${nombreColumna} excede ${maxLength} caracteres` };
  }

  // Verificar contenido peligroso
  if (containsDangerousContent(str)) {
    return { valido: false, error: `${nombreColumna} contiene caracteres no permitidos (XSS/SQL)` };
  }

  // Sanitizar el string
  const sanitizado = sanitizeText(str);

  return { valido: true, valorSanitizado: sanitizado };
}

/**
 * Valida y sanitiza un valor de celda Excel como email.
 * Aplica validación de formato y sanitización.
 * 
 * @param value - Valor de la celda
 * @param nombreColumna - Nombre de la columna para mensajes de error
 * @param requerido - Si es requerido (default: true)
 * @returns Resultado de validación con el email sanitizado
 */
export function validarCeldaEmail(
  value: unknown,
  nombreColumna: string,
  requerido = true
): ExcelCellValidation {
  // Null o undefined
  if (value === null || value === undefined) {
    if (requerido) {
      return { valido: false, error: `${nombreColumna} es requerido` };
    }
    return { valido: true, valorSanitizado: '' };
  }

  // Convertir a string
  const str = String(value).trim();

  if (str === '' && requerido) {
    return { valido: false, error: `${nombreColumna} es requerido` };
  }

  if (str === '' && !requerido) {
    return { valido: true, valorSanitizado: '' };
  }

  // Verificar contenido peligroso
  if (containsDangerousContent(str)) {
    return { valido: false, error: `${nombreColumna} contiene caracteres no permitidos` };
  }

  // Sanitizar email
  const sanitizado = sanitizeEmail(str);

  // Validar formato
  if (!isValidEmail(sanitizado)) {
    return { valido: false, error: `${nombreColumna} no tiene formato válido de email` };
  }

  return { valido: true, valorSanitizado: sanitizado };
}

/**
 * Valida un valor de celda Excel como fecha.
 * Acepta: Date, número (serial Excel), string fecha.
 * 
 * @param value - Valor de la celda
 * @param nombreColumna - Nombre de la columna para mensajes de error
 * @param requerido - Si es requerido (default: true)
 * @returns Resultado de validación con la fecha parseada
 */
export function validarCeldaFecha(
  value: unknown,
  nombreColumna: string,
  requerido = true
): ExcelCellValidation {
  const construirFechaEstrica = (anio: number, mes: number, dia: number): Date | null => {
    if (!Number.isInteger(anio) || !Number.isInteger(mes) || !Number.isInteger(dia)) {
      return null;
    }
    if (mes < 1 || mes > 12 || dia < 1 || dia > 31) {
      return null;
    }

    const fecha = new Date(anio, mes - 1, dia);
    if (
      isNaN(fecha.getTime())
      || fecha.getFullYear() !== anio
      || fecha.getMonth() !== mes - 1
      || fecha.getDate() !== dia
    ) {
      return null;
    }
    return fecha;
  };

  // Null o undefined
  if (value === null || value === undefined) {
    if (requerido) {
      return { valido: false, error: `${nombreColumna} es requerido` };
    }
    return { valido: true, valorSanitizado: undefined };
  }

  // Ya es Date
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return { valido: false, error: `${nombreColumna} no es una fecha válida` };
    }
    return { valido: true, valorSanitizado: value };
  }

  // Número (serial de Excel - días desde 1900-01-01)
  if (typeof value === 'number') {
    // Excel usa epoch 1899-12-30
    const excelEpoch = new Date(1899, 11, 30);
    const fecha = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
    if (isNaN(fecha.getTime())) {
      return { valido: false, error: `${nombreColumna} no es una fecha válida` };
    }
    return { valido: true, valorSanitizado: fecha };
  }

  // String - intentar parsear
  if (typeof value === 'string') {
    const str = value.trim();
    
    if (str === '' && !requerido) {
      return { valido: true, valorSanitizado: undefined };
    }

    if (str === '' && requerido) {
      return { valido: false, error: `${nombreColumna} es requerido` };
    }

    // Verificar contenido peligroso
    if (containsDangerousContent(str)) {
      return { valido: false, error: `${nombreColumna} contiene caracteres no permitidos` };
    }

    const ymdMatch = str.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
    if (ymdMatch) {
      const anio = parseInt(ymdMatch[1], 10);
      const mes = parseInt(ymdMatch[2], 10);
      const dia = parseInt(ymdMatch[3], 10);
      const fecha = construirFechaEstrica(anio, mes, dia);
      if (!fecha) {
        return { valido: false, error: `${nombreColumna} no tiene formato de fecha válido` };
      }
      return { valido: true, valorSanitizado: fecha };
    }

    const dmyMatch = str.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (dmyMatch) {
      const dia = parseInt(dmyMatch[1], 10);
      const mes = parseInt(dmyMatch[2], 10);
      const anio = parseInt(dmyMatch[3], 10);
      const fecha = construirFechaEstrica(anio, mes, dia);
      if (!fecha) {
        return { valido: false, error: `${nombreColumna} no tiene formato de fecha válido` };
      }
      return { valido: true, valorSanitizado: fecha };
    }

    // Intentar parsear fecha
    const fecha = new Date(str);
    if (isNaN(fecha.getTime())) {
      return { valido: false, error: `${nombreColumna} no tiene formato de fecha válido` };
    }
    return { valido: true, valorSanitizado: fecha };
  }

  return { valido: false, error: `${nombreColumna} tiene un tipo de dato inválido` };
}

/**
 * Tipo de validación para columnas Excel
 */
export type TipoValidacionExcel = 'numerico' | 'string' | 'email' | 'fecha';

/**
 * Configuración de validación para una columna Excel
 */
export interface ConfigValidacionColumna {
  /** Nombre de la columna */
  nombre: string;
  /** Tipo de validación */
  tipo: TipoValidacionExcel;
  /** Si es requerido */
  requerido?: boolean;
  /** Longitud máxima (solo para strings) */
  maxLength?: number;
}

/**
 * Valida una fila completa de Excel según configuración de columnas.
 * 
 * @param fila - Objeto con los valores de la fila
 * @param configuracion - Array de configuración de columnas
 * @returns Array de errores encontrados (vacío si todo es válido)
 */
export function validarFilaExcel(
  fila: Record<string, unknown>,
  configuracion: ConfigValidacionColumna[]
): string[] {
  const errores: string[] = [];

  for (const config of configuracion) {
    const valor = fila[config.nombre];
    let resultado: ExcelCellValidation;

    switch (config.tipo) {
      case 'numerico':
        resultado = validarCeldaNumerica(valor, config.nombre, config.requerido ?? true);
        break;
      case 'string':
        resultado = validarCeldaString(valor, config.nombre, config.requerido ?? true, config.maxLength);
        break;
      case 'email':
        resultado = validarCeldaEmail(valor, config.nombre, config.requerido ?? true);
        break;
      case 'fecha':
        resultado = validarCeldaFecha(valor, config.nombre, config.requerido ?? true);
        break;
    }

    if (!resultado.valido && resultado.error) {
      errores.push(resultado.error);
    }
  }

  return errores;
}

/**
 * Configuración predefinida para validar filas de Creación de Citas
 */
export const CONFIG_CREACION_CITA: ConfigValidacionColumna[] = [
  { nombre: 'Orden', tipo: 'numerico', requerido: true },
  { nombre: 'Plan', tipo: 'numerico', requerido: true },
  { nombre: 'Versión', tipo: 'numerico', requerido: true },
  { nombre: 'Actividad', tipo: 'numerico', requerido: true },
  { nombre: 'Duración', tipo: 'numerico', requerido: true },
  { nombre: 'Cantidad Programada', tipo: 'numerico', requerido: false },
  { nombre: 'Dni Consultor', tipo: 'string', requerido: true, maxLength: 20 },
  { nombre: 'Tipo de Programación', tipo: 'string', requerido: true, maxLength: 50 },
  { nombre: 'Observación', tipo: 'string', requerido: false, maxLength: 500 },
  { nombre: 'Contacto', tipo: 'string', requerido: false, maxLength: 100 },
  { nombre: 'Fecha Programación', tipo: 'fecha', requerido: true },
  { nombre: 'Email Contacto', tipo: 'email', requerido: false }
];

/**
 * Configuración predefinida para validar filas de Cancelación de Citas
 */
export const CONFIG_CANCELACION_CITA: ConfigValidacionColumna[] = [
  { nombre: 'Programación', tipo: 'numerico', requerido: true },
  { nombre: 'Observación', tipo: 'string', requerido: true, maxLength: 200 }
];

// ============================================
// REGLAS DEL LEGACY PARA CONTACTOS (paridad funcional)
// ============================================

/** Longitud máxima de un correo completo (RFC 5321). */
export const EMAIL_MAX_LENGTH = 254;

/** Longitud máxima de la parte local de un correo, antes de la arroba (RFC 5321). */
export const EMAIL_LOCAL_MAX_LENGTH = 64;

/**
 * Constants.REGEX_EMAILS del legacy. Lo usan: contactos de persona (AdminContactosViewImpl),
 * popup de contactos del centro de trabajo (ListaContactosViewImpl / SearchBenefContactsViewImpl)
 * y el popup "Editar contacto" de Programar Servicios.
 *
 * Mismo lenguaje que la regex original
 * `^([0-9a-zA-Z]([-.\w]*[0-9a-zA-Z_])*@([0-9a-zA-Z][-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$`, reescrita
 * sin ambigüedad (typescript:S5852). La original tenía `([-.\w]*[0-9a-zA-Z_])*`: la clase
 * `[-.\w]` incluye a `[0-9a-zA-Z_]`, así que una cadena como 'a' * 30 + '!' se podía repartir entre
 * las repeticiones de 2^n formas (backtracking exponencial, ~0,8 s con 30 caracteres).
 *  - Parte local: `[0-9a-zA-Z](?:[-.]*[0-9a-zA-Z_])*`. Cada repetición es "ceros o más `-` o `.` y
 *    luego un carácter de `[0-9a-zA-Z_]`": las clases no se solapan y cada cadena tiene una única
 *    descomposición.
 *  - Etiqueta de dominio: `[0-9a-zA-Z](?:[-_]*[0-9a-zA-Z])+` (mínimo 2 caracteres, como el legacy).
 * Verificada contra la original sobre 19,2 millones de cadenas (todas las de hasta 8 caracteres de un
 * alfabeto de 8 símbolos) más 322 mil de fuzz: sin ninguna diferencia.
 */
export const REGEX_EMAILS_LEGACY =
  /^[0-9a-zA-Z](?:[-.]*[0-9a-zA-Z_])*@(?:[0-9a-zA-Z](?:[-_]*[0-9a-zA-Z])+\.)+[a-zA-Z]{2,9}$/;

/**
 * Constants.REGEX_EMAIL del legacy (Utilidades.isValidEmail). Lo usan los contactos adicionales
 * del cumplimiento y los contactos del informe.
 *
 * Mismo lenguaje que la original `^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(?:[a-zA-Z]{2,6})$`, reescrita
 * para que `.` no pertenezca a la vez a la clase del dominio y al literal `\.` siguiente
 * (typescript:S5852): el dominio son etiquetas sin punto, cada una terminada en punto. La original
 * rechaza el dominio '.tld' (necesita al menos un carácter antes del último punto) pero acepta
 * '..tld' y '@a..com'; el lookahead `(?!\.[a-zA-Z]{2,6}$)` conserva exactamente esa regla.
 */
export const REGEX_EMAIL_LEGACY =
  /^[a-zA-Z0-9._%+-]+@(?!\.[a-zA-Z]{2,6}$)(?:[a-zA-Z0-9-]*\.)+[a-zA-Z]{2,6}$/;

/**
 * Defensa en profundidad antes de ejecutar la regex: acota el trabajo del motor a una entrada de
 * longitud razonable. Un correo sin arroba pasa este filtro y lo rechaza la regex.
 */
function longitudEmailAceptable(email: string): boolean {
  return email.length <= EMAIL_MAX_LENGTH && email.indexOf('@') <= EMAIL_LOCAL_MAX_LENGTH;
}

/**
 * Valida el email con Constants.REGEX_EMAILS del legacy. Aplica trim(); un valor nulo, vacío, de más
 * de 254 caracteres o con parte local de más de 64 se rechaza sin ejecutar la regex.
 */
export function isValidEmailContactoLegacy(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const email = value.trim();
  return longitudEmailAceptable(email) && REGEX_EMAILS_LEGACY.test(email);
}

/**
 * Valida el email con Constants.REGEX_EMAIL del legacy (Utilidades.isValidEmail). Mismas reglas de
 * trim y de longitud que {@link isValidEmailContactoLegacy}.
 */
export function isValidEmailLegacy(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const email = value.trim();
  return longitudEmailAceptable(email) && REGEX_EMAIL_LEGACY.test(email);
}

/**
 * Utilidades.cleanString + toUpperCase del legacy (getTextBoxChangeHandler(true)):
 * reemplaza letras acentuadas por su equivalente ASCII, elimina todo lo que no sea letra
 * o espacio y pasa a mayúsculas. Lo aplica el popup de contactos del centro de trabajo
 * a Nombre y Cargo.
 */
export function normalizarTextoLegacy(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '') // marcas diacríticas (tildes, diéresis, virgulilla)
    .replace(/[^a-zA-Z\s]+/g, '')
    .toUpperCase();
}
